import { useNftx } from '@nftx/react';
import { Address, BadRequestError, addressEqual } from 'nftx.js';
import useVaults from '../vaults/useVaults';
import { useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../queryKeys';

const useTopStakers = ({
  collectionAddress,
  vaultId,
}: {
  collectionAddress: Address | undefined;
  vaultId?: string;
}) => {
  const {
    network,
    core: { fetchInventoryPositions, fetchLiquidityPositions },
  } = useNftx();
  const { data: vaults } = useVaults();
  const enabled = !!(collectionAddress && vaults);

  return useQuery(
    [QueryKey.TOP_STAKERS, network, collectionAddress, vaults, vaultId],
    async () => {
      if (!collectionAddress || !vaults) {
        throw new BadRequestError();
      }

      const vaultIds = vaultId
        ? [vaultId]
        : vaults
            .filter((v) => addressEqual(v.asset.id, collectionAddress))
            .map((v) => v.vaultId);
      const ips = await Promise.all(
        vaultIds.map((vaultId) => fetchInventoryPositions({ network, vaultId }))
      );
      const lps = await Promise.all(
        vaultIds.map((vaultId) => fetchLiquidityPositions({ network, vaultId }))
      );

      const stakers: Record<
        string,
        { userAddress: Address; poolShare: bigint; value: bigint }
      > = {};

      ips.flat().forEach((position) => {
        let staker = stakers[position.userAddress];
        if (!staker) {
          staker = stakers[position.userAddress] = {
            poolShare: 0n,
            value: 0n,
            userAddress: position.userAddress,
          };
        }
        staker.poolShare += position.poolShare;
        staker.value += position.vTokenValue;
      });
      lps.flat().forEach((position) => {
        let staker = stakers[position.userAddress];
        if (!staker) {
          staker = stakers[position.userAddress] = {
            poolShare: 0n,
            value: 0n,
            userAddress: position.userAddress,
          };
        }
        staker.poolShare += position.poolShare;
        staker.value += position.value;
      });

      return Object.values(stakers)
        .sort((a, b) => {
          if (a.value > b.value) {
            return -1;
          }
          if (a.value < b.value) {
            return 1;
          }
          return 0;
        })
        .slice(0, 10);
    },
    { enabled }
  );
};

export default useTopStakers;
